import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus, MusicNotes, DownloadSimple } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { creatorHref, audioHref } from "../lib/links";
import SmartImg from "./SmartImg";
import FranchiseMark from "./FranchiseMark";
import { EDITS_LIVE } from "../lib/features";

// Fetched the first time a menu opens, then kept for the visit.
const cache = {};
function useMenuData(key, loader) {
  const [data, setData] = useState(cache[key] || null);
  useEffect(() => {
    if (cache[key]) { setData(cache[key]); return; }
    let live = true;
    loader().then((d) => { cache[key] = d; if (live) setData(d); }).catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return data;
}

const COLUMN = "min-w-0";
const Eyebrow = ({ children }) => <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF8090] mb-3">{children}</div>;

function Lead({ title, text, to, label, extra }) {
  return (
    <div className={COLUMN}>
      <h3 className="font-display font-black text-3xl tracking-tighter mb-2">{title}</h3>
      <p className="text-sm text-white/55 leading-relaxed mb-5 max-w-[260px]">{text}</p>
      <Link to={to} className="btn-primary !py-2.5 !px-5 !text-xs">{label} <ArrowRight size={14} /></Link>
      {extra && <Link to={extra.to} className="mt-3 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-white/45 hover:text-[#FF8090] transition"><Plus size={11} /> {extra.label}</Link>}
    </div>
  );
}

function LinkList({ title, items }) {
  return (
    <div className={COLUMN}>
      <Eyebrow>{title}</Eyebrow>
      <ul className="space-y-0.5">
        {items.map(([label, to, hint]) => (
          <li key={label}>
            <Link to={to} className="group flex items-center justify-between rounded-lg px-3 py-2 -mx-3 text-sm font-display font-bold text-white/75 hover:text-white hover:bg-white/[0.06] transition">
              {label}
              <span className="flex items-center gap-2">
                {hint && <span className="text-[10px] font-mono font-normal text-white/30">{hint}</span>}
                <ArrowRight size={12} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Creators({ title, people, hrefFor, countFor }) {
  return (
    <div className={COLUMN}>
      <Eyebrow>{title}</Eyebrow>
      <div className="space-y-1">
        {(people || Array.from({ length: 4 })).slice(0, 5).map((c, i) => c ? (
          <Link key={c.id} to={hrefFor(c)} className="group flex items-center gap-3 rounded-lg px-3 py-1.5 -mx-3 hover:bg-white/[0.06] transition">
            <span className="w-9 h-9 rounded-full overflow-hidden bg-[#232323] border border-white/10 group-hover:border-[#FF8090]/60 flex items-center justify-center flex-shrink-0 transition">
              {c.avatar ? <SmartImg src={c.avatar} width={96} still alt="" className="w-full h-full object-cover" /> : <span className="font-display font-bold text-xs text-white/60">{(c.name || "?")[0]}</span>}
            </span>
            <span className="min-w-0">
              <span className="block text-[13px] font-display font-bold truncate text-white/85 group-hover:text-white">{c.name}</span>
              <span className="block text-[10px] font-mono text-white/35">{countFor(c)}</span>
            </span>
          </Link>
        ) : <div key={i} className="h-9 rounded-lg bg-white/[0.04] animate-pulse my-1.5" />)}
      </div>
    </div>
  );
}

const Poster = ({ to, image, label, sub, tall }) => (
  <Link to={to} className="group relative block rounded-xl overflow-hidden border border-white/10 hover:border-white/30 bg-[#1A1A1A] transition">
    <div className={`${tall ? "aspect-[9/14]" : "aspect-[4/5]"} relative`}>
      {image && <SmartImg src={image} width={320} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-2.5">
        <div className="font-display font-bold text-[12px] leading-tight line-clamp-2">{label}</div>
        {sub && <div className="text-[9px] font-mono text-white/45 mt-0.5 truncate">{sub}</div>}
      </div>
    </div>
  </Link>
);

function ScenepacksMenu() {
  const movies = useMenuData("movies", () => api.get("/movies?limit=4&sort=trending").then((r) => r.data));
  const franchises = useMenuData("franchises", () => api.get("/franchises").then((r) => r.data)) || [];
  const creators = useMenuData("pack-creators", () => api.get("/creators?limit=60").then((r) => r.data.filter((c) => c.scenepacks_count > 0).slice(0, 5)));
  return (
    <div className="grid grid-cols-[1.1fr_1fr_1fr_1.5fr] gap-10">
      <Lead title="Scenepacks" text="Movies and TV shows, character by character — every version in one place." to="/scenepacks" label="Browse all" extra={{ to: "/requests?type=scenepack", label: "Request a scenepack" }} />
      <div className={COLUMN}>
        <LinkList title="Quality" items={[["4K REMUX", "/scenepacks?quality=4K%20REMUX"], ["4K", "/scenepacks?quality=4K"], ["1080P REMUX", "/scenepacks?quality=1080P%20REMUX"], ["1080P", "/scenepacks?quality=1080P"]]} />
        <div className="flex flex-wrap gap-1.5 mt-4">
          {["Action", "Sci-Fi", "Drama", "Romance", "Thriller", "Horror"].map((g) => (
            <Link key={g} to={`/scenepacks?genre=${g}`} className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/10 text-white/55 hover:text-white hover:border-[#FF8090]/60 transition">{g}</Link>
          ))}
        </div>
        {franchises.length > 0 && (
          <div className="mt-5">
            <Eyebrow>Tabs</Eyebrow>
            {franchises.map((f) => (
              <Link key={f.slug} to={`/${f.slug}`} className="group inline-flex items-center gap-2 mr-3">
                <FranchiseMark name={f.name} size="sm" className="group-hover:scale-105 transition-transform" />
              </Link>
            ))}
          </div>
        )}
      </div>
      <Creators title="Top creators" people={creators} hrefFor={creatorHref} countFor={(c) => `${c.scenepacks_count} scenepacks`} />
      <div className={COLUMN}>
        <Eyebrow>Trending now</Eyebrow>
        <div className="grid grid-cols-4 gap-2.5">
          {(movies || Array.from({ length: 4 })).map((m, i) => m
            ? <Poster key={m.slug} to={`/scenepacks/movie/${m.slug}`} image={m.thumbnail_url} label={m.movie_title} sub={`${m.characters_count} characters`} />
            : <div key={i} className="aspect-[4/5] rounded-xl bg-white/[0.04] animate-pulse" />)}
        </div>
      </div>
    </div>
  );
}

function AudiosMenu() {
  const trending = useMenuData("audios", () => api.get("/audios/browse?sort=trending&limit=4").then((r) => r.data.items));
  const creators = useMenuData("audio-creators", () => api.get("/audios/creators?limit=5").then((r) => r.data));
  return (
    <div className="grid grid-cols-[1.1fr_1fr_1fr_1.5fr] gap-10">
      <Lead title="Audios" text="Aesthetic, soft, film & velo cuts. Hear them with a waveform before you download." to="/audios" label="Browse all" extra={{ to: "/requests?type=audio", label: "Request an audio" }} />
      <LinkList title="By style" items={[["Aesthetic", "/audios?style=aesthetic"], ["Soft", "/audios?style=soft"], ["Film", "/audios?style=film"], ["Velo", "/audios?style=velo"], ["Trending", "/audios?sort=trending", "hot"]]} />
      <Creators title="Top creators" people={creators} hrefFor={(c) => `/audios?creator_id=${c.id}`} countFor={(c) => `${c.audios_count} audios`} />
      <div className={COLUMN}>
        <Eyebrow>Trending now</Eyebrow>
        <div className="space-y-1.5">
          {(trending || Array.from({ length: 4 })).map((a, i) => a ? (
            <Link key={a.id} to={audioHref(a)} className="group flex items-center gap-3 rounded-lg px-2.5 py-1.5 -mx-1 hover:bg-white/[0.06] transition">
              <span className="w-10 h-10 rounded-md overflow-hidden bg-[#232323] flex items-center justify-center flex-shrink-0">
                {a.cover_url ? <SmartImg src={a.cover_url} width={96} still alt="" className="w-full h-full object-cover" /> : <MusicNotes size={16} className="text-white/30" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-display font-bold truncate text-white/85 group-hover:text-white">{a.title}</span>
                <span className="block text-[10px] font-mono text-white/35 truncate">{a.artist || a.creator_name}</span>
              </span>
              <span className="text-[10px] font-mono text-white/30 flex items-center gap-1"><DownloadSimple size={10} />{a.downloads}</span>
            </Link>
          ) : <div key={i} className="h-10 rounded-lg bg-white/[0.04] animate-pulse" />)}
        </div>
      </div>
    </div>
  );
}

function EditsMenu() {
  const edits = useMenuData("edits", () => api.get("/edits?limit=5").then((r) => r.data));
  return (
    <div className="grid grid-cols-[1.1fr_3.5fr] gap-10">
      <Lead title="Edits" text="Edits made with ALPHA scenepacks and audios. Watch, get inspired, credit the editors." to="/edits" label="Watch edits" />
      <div className={COLUMN}>
        <Eyebrow>Latest</Eyebrow>
        <div className="grid grid-cols-5 gap-3">
          {(edits || Array.from({ length: 5 })).map((e, i) => e
            ? <Poster key={e.id} to={`/edits?edit=${e.id}`} image={e.thumbnail_url} label={e.title} sub={`@${e.creator_name}`} tall />
            : <div key={i} className="aspect-[9/14] rounded-xl bg-white/[0.04] animate-pulse" />)}
        </div>
      </div>
    </div>
  );
}

function EditsSoonMenu() {
  return (
    <div className="grid grid-cols-[1.1fr_3.5fr] gap-10 items-center">
      <Lead title="Edits" text="The home for every edit made with ALPHA scenepacks and audios. Almost here." to="/edits" label="See what's coming" />
      <div className="flex items-center gap-8">
        <span className="soon-pill inline-flex items-center gap-2 rounded-full border border-[#FF8090]/60 bg-gradient-to-r from-[#880015] to-[#C2102E] px-7 py-3">
          <span className="font-display font-black text-2xl tracking-[0.35em] pl-[0.35em] text-white">SOON</span>
        </span>
        <div className="text-sm text-white/45 max-w-xs leading-relaxed">A vertical feed of edits, each linked to the scenepack and audio it was made with.</div>
      </div>
    </div>
  );
}

const MENUS = { scenepacks: ScenepacksMenu, audios: AudiosMenu, edits: EDITS_LIVE ? EditsMenu : EditsSoonMenu };

/** The panel that drops under the navbar while a section's link is hovered. */
export default function MegaPanel({ which }) {
  const Menu = MENUS[which];
  if (!Menu) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
      className="absolute left-0 right-0 top-full border-t border-white/10 bg-[#121212]/[0.985] backdrop-blur-2xl shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)]"
      data-testid={`mega-${which}`}
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#880015] to-transparent" />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-9">
        <motion.div key={which} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
          <Menu />
        </motion.div>
      </div>
    </motion.div>
  );
}
